/**
 * The counter receipt as a printable web page.
 *
 * Desktop sends ESC/POS bytes straight to a network printer. A browser cannot
 * open a socket, so on the web the "80mm" button used to fail every time. The
 * receipt a browser CAN print is a page: the same text lines the thermal
 * builder produces, set in a monospace face sized so one line of the roll's
 * characters exactly fills the printable width. The shop picks its receipt
 * printer in the print dialog (its Windows driver handles the paper), and the
 * page tells the dialog the roll width so nothing is scaled to A4.
 */
import { escapeHtml } from './invoiceFormat';
import { getReceiptProfile } from '../engine/ReceiptProfiles';
import type { PaperWidth } from '../contracts/PrintTypes';

/** Printable width of each roll, in mm (the head is narrower than the paper). */
const PRINTABLE_MM: Record<PaperWidth, number> = { 58: 48, 80: 72, 112: 104 };

/** A monospace glyph is ~0.6em wide; size the font so a full line fits. */
const GLYPH_EM = 0.6;

export function buildReceiptHtml(text: string, paperWidth: PaperWidth, logoDataUrl?: string | null): string {
  const cols = getReceiptProfile(paperWidth).charsPerLine;
  const printable = PRINTABLE_MM[paperWidth];
  const fontMm = (printable / cols / GLYPH_EM).toFixed(3);
  // Only an embedded image: the print frame has no network, and anything else
  // would be missing from the page (see logoImage.ts).
  const logo = logoDataUrl && logoDataUrl.startsWith('data:image/')
    ? `<img class="logo" src="${escapeHtml(logoDataUrl)}" alt="">`
    : '';
  return `<!doctype html><html><head><meta charset="utf-8"><title>Receipt</title><style>
@page { size: ${paperWidth}mm auto; margin: 0; }
html, body { margin: 0; padding: 0; background: #fff; color: #000; }
body { width: ${printable}mm; margin: 0 auto; padding: 3mm 0 6mm; }
.logo { display: block; max-width: 60%; max-height: 22mm; margin: 0 auto 2mm; }
pre { margin: 0; font: ${fontMm}mm/1.25 "Courier New", Consolas, monospace; white-space: pre; }
</style></head><body>${logo}<pre>${escapeHtml(text.replace(/\n+$/, ''))}</pre></body></html>`;
}
